import { Form } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import { useLazyGetPositionsForSystemQuery } from '@app/api/Positions';
import {
  useLazyGetEmployeesSearchResultQuery,
  useSearchEmployeesMutation,
} from '@app/api/Users';
import { EDIT_EMPLOYEE, FRONT_WARNING, MOBILE } from '@app/constants';
import { navigation } from '@app/data';
import { useUserPrint, useWindowSize } from '@app/hooks';
import { HeaderContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useRegistryFilterState } from '@app/shared/hooks';
import type { NAnalytics, NSmallGlobalEntities } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import type { NNavigation } from '@app/types/Navigation';
import { addEnterKeyListener, changeStandardPaginationUi } from '@app/utils';

import type { BaseOptionType } from 'antd/es/select';

const INITIAL_ROWS_COUNT = 20;

export const useEmployees = () => {
  const { activeBranchId } = useUserPrint();

  const [isLoading, setIsLoading] = useState(false);
  // Выбранные фильтры переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161): хранилище вкладочное, как у реестра
  // записей и реестра задач.
  const [selectedStatus, setSelectedStatus] = useRegistryFilterState<
    BaseOptionType[] | null
  >('employees', 'selectedStatus', null);

  const [selectedPositions, setSelectedPositions] = useRegistryFilterState<
    BaseOptionType[] | null
  >('employees', 'selectedPositions', null);
  const [selectedBranches, setSelectedBranches] = useRegistryFilterState<
    BaseOptionType[] | null
  >('employees', 'selectedBranches', null);
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [searchError, setSearchError] = useState('');
  const [allBranches, setAllBranches] = useState<NBranches.BranchesForSystem[]>(
    []
  );
  const [employees, setEmployees] = useState<NAnalytics.ModelEmployeeLite[]>(
    []
  );
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_COUNT);
  const [searchId, setSearchId] = useState<number | null>(null);

  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= MOBILE;

  const [form] = Form.useForm();

  const { showNotification } = useContext(NotificationContext);

  const navigate = useNavigate();

  const { setHeader } = useContext(HeaderContext);

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [searchEmployees] = useSearchEmployeesMutation();
  const [getEmployeesSearchResult] = useLazyGetEmployeesSearchResultQuery();

  const getDefaultBranch = useCallback(
    (allBranches: NBranches.BranchesForSystem[]) => {
      const defaultBranch = allBranches.find(
        (branch: NBranches.BranchesForSystem) => branch.Id === activeBranchId
      );
      if (defaultBranch) {
        const adaptedDefaultBranch = {
          label: defaultBranch.Name,
          value: defaultBranch.Id,
        };
        setSelectedBranches([adaptedDefaultBranch]);
        return adaptedDefaultBranch;
      } else {
        return [];
      }
    },
    [activeBranchId, setSelectedBranches]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    setSelectedBranches([getDefaultBranch(allBranches)]);
    setSelectedPositions([]);
    setSelectedStatus([]);
    setSearchId(null);

    const fetchEmployees = async () => {
      try {
        setIsLoading(true);

        if (!activeBranchId)
          return showNotification(
            'Идентификатор активного филиала не найден',
            FRONT_WARNING
          );

        const { Employees, CountEmployees, Id } = await searchEmployees({
          ActiveBranchId: activeBranchId,
          data: {
            SearchTerm: null,
            BranchIds: [activeBranchId],
            PositionIds: null,
            IsDeleted: null,
            IsTerminated: false,
            UsersPerPage: INITIAL_ROWS_COUNT,
          },
        }).unwrap();

        setRowsPerPage(INITIAL_ROWS_COUNT);
        setEmployees(Employees);
        setTotalRowsCount(CountEmployees);
        setSearchId(Id);
        setHeader({ searchId: Id });
        setPageNumber(1);
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, [
    activeBranchId,
    allBranches,
    form,
    getDefaultBranch,
    searchEmployees,
    showNotification,
    setHeader,
    setSelectedBranches,
    setSelectedPositions,
    setSelectedStatus,
  ]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (activeBranchId) {
        setIsLoading(true);

        try {
          const positionsData = await getPositionsForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setPositions([
            { Id: 0, IdS: null, Name: 'Без должности' },
            ...positionsData,
          ]);

          const branchesData = await getBranchesForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setAllBranches(branchesData);
          getDefaultBranch(branchesData);

          const employeeData = await searchEmployees({
            ActiveBranchId: activeBranchId,
            data: {
              SearchTerm: null,
              BranchIds: [activeBranchId],
              PositionIds: null,
              IsDeleted: null,
              IsTerminated: false,
              UsersPerPage: INITIAL_ROWS_COUNT,
            },
          }).unwrap();

          setHeader({ searchId: employeeData.Id });
          setEmployees(employeeData.Employees);
          setTotalRowsCount(employeeData.CountEmployees);
          setSearchId(employeeData.Id);
        } catch (error) {
          showNotification(handleCatchError(error));
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchInitialData();

    const removeListener = addEnterKeyListener({
      callback: form.submit,
    });

    return removeListener;
  }, [
    activeBranchId,
    form.submit,
    getDefaultBranch,
    showNotification,
    setHeader,
    getBranchesForSystem,
    getPositionsForSystem,
    searchEmployees,
  ]);

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
    });
  }, [employees, isMobile]);

  const submitForm = useCallback(() => {
    const selectedBranchIds =
      selectedBranches?.map(({ value }) => Number(value)) || [];
    const selectedPositionIds =
      selectedPositions?.map(({ value }) => Number(value)) || [];
    const selectedStatusLabels =
      selectedStatus?.map((status) => status.value === 'archived') || [];

    const fetchEmployees = async () => {
      try {
        setIsLoading(true);

        const { Employees, CountEmployees, Id } = await searchEmployees({
          ActiveBranchId: activeBranchId,
          data: {
            SearchTerm: form.getFieldValue('search')
              ? form.getFieldValue('search')
              : '',
            BranchIds: defineValue(selectedBranchIds),
            PositionIds: defineValue(selectedPositionIds),
            IsDeleted:
              selectedStatusLabels.length === 2 || 0
                ? null
                : selectedStatusLabels[0],
            UsersPerPage: rowsPerPage,
          },
        }).unwrap();

        setEmployees(Employees);
        setTotalRowsCount(CountEmployees);
        setSearchId(Id);
        setHeader({ searchId: Id });
        setPageNumber(1);
      } catch (error) {
        setSearchError(handleCatchError(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, [
    activeBranchId,
    rowsPerPage,
    searchEmployees,
    selectedBranches,
    selectedPositions,
    selectedStatus,
    form,
    setHeader,
  ]);

  const handleEdit = (id: number) => {
    const isEmployeeDeleted = employees.find(
      (employee) => employee.Id === id
    )?.IsDeleted;
    const item = navigation.find(
      (item): item is NNavigation.SidebarItem =>
        'id' in item && item.id === EDIT_EMPLOYEE
    );
    const path = item?.path;
    navigate(`${path}?id=${id}&key=${isEmployeeDeleted ? 1 : 2}`);
  };

  const defineValue = (selectedValues: number[] | null) =>
    Array.isArray(selectedValues) && selectedValues.length > 0
      ? selectedValues
      : null;

  const handleChange = (page: number, pageSize: number) => {
    if (searchId) {
      const fetchEmployeesSearchResult = async () => {
        setIsLoading(true);
        try {
          const { Employees, PageN } = await getEmployeesSearchResult({
            ActiveBranchId: activeBranchId,
            searchId,
            page: pageSize === rowsPerPage ? page : 1,
            rowsPerPage: pageSize,
          }).unwrap();

          setEmployees(Employees);
          setRowsPerPage(pageSize);
          setPageNumber(PageN);
        } catch (error) {
          showNotification(handleCatchError(error));
        } finally {
          setIsLoading(false);
        }
      };

      fetchEmployeesSearchResult();
    }
  };

  return {
    isLoading,
    form,
    submitForm,
    selectedStatus,
    setSelectedStatus,
    positions,
    selectedPositions,
    setSelectedPositions,
    allBranches,
    selectedBranches,
    setSelectedBranches,
    employees,
    handleEdit,
    totalRowsCount,
    pageNumber,
    rowsPerPage,
    handleChange,
    handleReset,
    searchError,
    setSearchError,
    navigate,
    isMobile,
  };
};
